import React from 'react';
import NavBar from '../NavBar/navBar';
import { Outlet } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';

const LayoutPage = () => {
  const { themeMode, toggleTheme, userData } = useAuth();
  const isDark = themeMode === 'dark';

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          background: {
            default: isDark ? '#0b0f19' : '#f8fafc',
            paper: isDark ? '#131b2e' : '#ffffff',
          },
          primary: {
            main: '#6366f1',
            light: '#818cf8',
            dark: '#4f46e5',
          },
          secondary: {
            main: '#06b6d4',
          },
          text: {
            primary: isDark ? '#f8fafc' : '#0f172a',
            secondary: isDark ? '#94a3b8' : '#64748b',
          },
        },
        typography: {
          fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                backgroundColor: isDark ? 'rgba(19, 27, 46, 0.85)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(16px)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 10,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : '#ffffff',
                backdropFilter: 'blur(20px)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
                color: isDark ? '#f8fafc' : '#0f172a',
              },
            },
          },
        },
      }),
    [isDark]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box 
        sx={{ 
          display: 'flex', 
          minHeight: '100vh',
          backgroundColor: isDark ? '#0b0f19' : '#f8fafc',
          backgroundImage: isDark
            ? 'radial-gradient(at 0% 0%, rgba(99, 102, 241, 0.12) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(6, 182, 212, 0.12) 0px, transparent 50%)'
            : 'radial-gradient(at 0% 0%, rgba(99, 102, 241, 0.06) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(6, 182, 212, 0.06) 0px, transparent 50%)',
          color: isDark ? '#f8fafc' : '#0f172a',
          transition: 'background-color 0.3s ease, color 0.3s ease'
        }}
      >
        <NavBar />

        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflowX: 'hidden'
          }}
        >
          {/* Top Header Bar with Theme Switcher */}
          <header className={`px-6 py-3 border-b flex items-center justify-between backdrop-blur-md transition-colors ${
            isDark 
              ? 'bg-slate-950/60 border-white/10 text-slate-100' 
              : 'bg-white/80 border-slate-200 text-slate-800'
          }`}>
            <div className="flex items-center space-x-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-indigo-400">AMN CMS</span>
              <span className="text-xs text-slate-400">/ Management Portal</span>
            </div>

            <div className="flex items-center space-x-3">
              <Tooltip title={`Switch to ${isDark ? 'Light' : 'Dark'} Mode`}>
                <IconButton 
                  onClick={toggleTheme} 
                  color="inherit" 
                  sx={{ 
                    p: 1, 
                    borderRadius: '0.625rem',
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {isDark ? <Brightness7Icon className="text-amber-400" fontSize="small" /> : <Brightness4Icon className="text-indigo-600" fontSize="small" />}
                </IconButton>
              </Tooltip>

              <div className="hidden sm:flex items-center space-x-2 px-3 py-1 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="font-semibold text-slate-300">{userData?.role || 'Admin'}</span>
              </div>
            </div>
          </header>

          {/* Main Route Content */}
          <div className="p-4 sm:p-6 md:p-8 flex-1">
            <Outlet />
          </div>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LayoutPage;
